import { useParams, useLocation, useNavigate } from "react-router-dom";
import SideMenu from "../Visualize/SideMenu/SideMenu";
import VisualizeByNameContent from './VisualizeByNameContent/VisualizeByNameContent';
import { VisualizeByNameContentContainer, RightContentContainer } from "./VisualizeByName.styles";
import { Breadcrumb } from "@mds/mds-reactjs-library";
import { Link } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import FileEndpoints from "../../Services/FileEndpoints";
import { useCreateToast } from "../../Hooks/useCreateToast";
import DataEndpoints from "../../Services/DataEndpoints";
import { Icon, TYPE_OUTLINE } from "@mds/mds-reactjs-library";
import Outline64Circle from '@mds/mds-icons/icons/svg/outline-64-circle.svg';
import { CHART_MENU_TYPES, filterChartCategoriesForMenu, isMarketSnapshotChart, useChartCategories } from "../../Hooks/useChartCategories";
import { NavigationRoutes } from "../../Constants/Routes";

const VisualizeByName = ({ menuType = CHART_MENU_TYPES.INTERACTIVE, chartBaseRoute = NavigationRoutes.VISUALIZE }) => {
    const { chartCategories, loading: categoriesLoading } = useChartCategories();
    const params = useParams();
    const location = useLocation();
    const navigate = useNavigate();
    const { state } = location;
    const { createErrorToast } = useCreateToast();
    const visibleChartCategories = useMemo(
        () => filterChartCategoriesForMenu(chartCategories, menuType),
        [chartCategories, menuType]
    );

    const chartNameFromRoute = params.name.split('-').join(' ');
    const pathParam = new URLSearchParams(location.search).get('path');
    const legacyCategoryParam = new URLSearchParams(location.search).get('category');
    const categoryPathParamDecoded = pathParam
        ? decodeURIComponent(pathParam)
        : legacyCategoryParam
            ? decodeURIComponent(legacyCategoryParam)
            : null;
    const [chart, setChart] = useState(null);
    const [chartConfiguration, setChartConfiguration] = useState(null);
    const [loading, setLoading] = useState(true);

    function resolveChartContext(data, title, preferredCategory, routeSegment) {
        const normalizedTitle = (title || '').trim().toLowerCase();
        const normalizedPreferredCategory = (preferredCategory || '').trim().toLowerCase();
        const normalizedRoute = (routeSegment || '').trim().toLowerCase();
        let firstMatch = null;

        for (const item of data) {
            const chart = item.charts.find((candidate) => {
                const candidateTitle = (candidate?.title || '').trim().toLowerCase();
                const candidateRoute = (candidate?.routeId ?? candidate?.title?.split(' ').join('-') ?? '').trim().toLowerCase();
                return candidateTitle === normalizedTitle
                    || (!!normalizedRoute && candidateRoute === normalizedRoute);
            });

            if (chart) {
                const match = { category: item, chartTitle: chart.title };
                if (!firstMatch) firstMatch = match;

                if (
                    !normalizedPreferredCategory ||
                    (item?.displayName || '').trim().toLowerCase() === normalizedPreferredCategory
                ) {
                    return match;
                }
            }
        }

        return firstMatch;
    }

    function resolveChartStoreContext(data, chartId) {
        for (const item of data || []) {
            const chart = item.charts.find((candidate) => candidate?.chartId === chartId);
            if (chart) {
                return { category: item, chart };
            }
        }
        return null;
    }

    const chartStoreContext = useMemo(
        () => resolveChartStoreContext(visibleChartCategories, params.name),
        [visibleChartCategories, params.name]
    );
    const useChartStore = !!chartStoreContext;
    const chartStoreChart = chartStoreContext?.chart || null;

    const resolvedChartContext = useMemo(
        () => (useChartStore ? null : resolveChartContext(visibleChartCategories, chartNameFromRoute, categoryPathParamDecoded, params.name)),
        [useChartStore, visibleChartCategories, chartNameFromRoute, categoryPathParamDecoded, params.name]
    );
    const resolvedChartName = useChartStore
        ? (chartStoreChart?.title || chartNameFromRoute)
        : (resolvedChartContext?.chartTitle || chartNameFromRoute);
    const resolvedCategoryName = useChartStore
        ? chartStoreContext?.category?.displayName
        : (resolvedChartContext?.category?.displayName || categoryPathParamDecoded);
    const showCategoryBreadcrumb = !!resolvedCategoryName
        && resolvedCategoryName.trim().toLowerCase() !== (resolvedChartName || '').trim().toLowerCase();

    const fallbackChartStoreContext = useMemo(
        () => resolveChartStoreContext(chartCategories, params.name),
        [chartCategories, params.name]
    );
    const fallbackResolvedChartContext = useMemo(
        () => (fallbackChartStoreContext
            ? null
            : resolveChartContext(chartCategories, chartNameFromRoute, categoryPathParamDecoded, params.name)),
        [fallbackChartStoreContext, chartCategories, chartNameFromRoute, categoryPathParamDecoded, params.name]
    );
    const fallbackChartTitle = fallbackChartStoreContext?.chart?.title || fallbackResolvedChartContext?.chartTitle || chartNameFromRoute;
    const fallbackCategoryName = fallbackChartStoreContext?.category?.displayName || fallbackResolvedChartContext?.category?.displayName || categoryPathParamDecoded;
    const resolvedBaseRoute = isMarketSnapshotChart({ title: fallbackChartTitle })
        ? NavigationRoutes.MARKET_SNAPSHOT
        : NavigationRoutes.VISUALIZE;

    useEffect(() => {
        if (categoriesLoading || resolvedBaseRoute === chartBaseRoute || !fallbackChartTitle) {
            return;
        }

        const query = fallbackCategoryName
            ? '?path=' + encodeURIComponent(fallbackCategoryName)
            : '';

        navigate(resolvedBaseRoute + '/' + params.name + query, {
            replace: true,
            state,
        });
    }, [categoriesLoading, resolvedBaseRoute, chartBaseRoute, fallbackChartTitle, fallbackCategoryName, navigate, params.name, state]);

    const LoadingSpinner = () => (
        <div className="loadingContainer">
            <Icon fill="#2251FF" type={TYPE_OUTLINE} src={Outline64Circle} />
        </div>
    );

    useEffect(() => {
        if (categoriesLoading) return;
        if (resolvedBaseRoute !== chartBaseRoute) return;
        setLoading(true);

        if (useChartStore) {
            if (!chartStoreChart) {
                setLoading(false);
                return;
            }

            const chartObject = {
                title: chartStoreChart.title,
                chartId: chartStoreChart.chartId,
                datasetId: chartStoreChart.datasetId,
                thumbnail: chartStoreChart.thumbnail,
                thumbnailMimetype: chartStoreChart.thumbnailMimetype,
                previewMimetype: chartStoreChart.previewMimetype,
            };

            let objectUrl = null;
            const finish = () => {
                setChart(chartObject);
                setChartConfiguration(JSON.parse(chartStoreChart.configuration || '{}'));
                setLoading(false);
            };

            if (chartStoreChart.hasPreview) {
                DataEndpoints.getChartPreview(chartStoreChart.chartId)
                    .then(({ data, headers }) => {
                        const blob = new Blob([data], { type: chartStoreChart.previewMimetype || headers['content-type'] });
                        objectUrl = URL.createObjectURL(blob);
                        chartObject.preview = objectUrl;
                    })
                    .catch(e => createErrorToast(e))
                    .finally(finish);
            } else {
                finish();
            }

            return () => {
                if (objectUrl) {
                    URL.revokeObjectURL(objectUrl);
                }
            };
        }

        const folderTitle = resolvedCategoryName;
        let getFilesPromise = FileEndpoints.getFiles(null, folderTitle, resolvedChartName);
        let getChartPromise = DataEndpoints.getChart(resolvedChartName, folderTitle);
        Promise.all([getFilesPromise, getChartPromise]).then(([{ data: fileResult }, { data: chartResult }]) => {
            let chart = fileResult?.[0];
            if (!chart) {
                return;
            }
            chart.chartId = chartResult.chartId;
            setChart(chart);
            setChartConfiguration(JSON.parse(chartResult.configuration));
        })
            .catch(e => createErrorToast(e))
            .finally(() => setLoading(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [resolvedChartName, resolvedCategoryName, categoryPathParamDecoded, useChartStore, visibleChartCategories, chartStoreChart, params.name, categoriesLoading, resolvedBaseRoute, chartBaseRoute]);

    return (
        <div>
            {
                chart
                    ? <div style={{ display: 'flex', background: "#f2f2f2" }}>
                        <SideMenu chartCategories={visibleChartCategories} chartBaseRoute={resolvedBaseRoute} />
                        <RightContentContainer>
                            <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                                <VisualizeByNameContentContainer>
                                    {showCategoryBreadcrumb ? (
                                        <Breadcrumb>
                                            <Link to={`${resolvedBaseRoute}?menutitle=${encodeURIComponent((resolvedCategoryName || '').split(' ').join('-'))}`}>
                                                {resolvedCategoryName}
                                            </Link>
                                            <span>{resolvedChartName}</span>
                                        </Breadcrumb>
                                    ) : (
                                        <div className="chartTitle">{resolvedChartName}</div>
                                    )}
                                    {loading ? (
                                        <div className="loadingWrapper">
                                            <LoadingSpinner />
                                        </div>
                                    ) : (
                                        chart && <VisualizeByNameContent chart={chart} chartConfiguration={chartConfiguration} reUseReport={state} />
                                    )}
                                </VisualizeByNameContentContainer>
                                <div style={{ flex: 1 }}></div>
                            </div>
                        </RightContentContainer>
                    </div> : null
            }
        </div>
    );
}

export default VisualizeByName;